import ApiHelper from "@/scripts/api/ApiHelper.js";
import {PollJobStatus} from "@/scripts/common/enums.js";

/**
 * Polls backend job status until completion, cancellation, or timeout.
 *
 * @param {string} jobId
 * @param {Object} options
 * @param {number} [options.pollInterval=2000]
 * @param {number} [options.maxAttempts=90]
 * @param {string} [options.failureMessage='Job failed.']
 * @param {string} [options.timeoutMessage='Job timed out.']
 * @param {Function} [options.shouldCancel] optional callback returning true when polling should stop early
 * @param {*} [options.cancelResult=null] value returned when shouldCancel triggers
 * @returns {Promise<any>} resolves with job result or cancelResult when cancelled
 */
export async function pollJob(jobId, options = {}) {
    const {
        pollInterval = 2000,
        maxAttempts = 90,
        failureMessage = "Job failed.",
        timeoutMessage = "Job timed out.",
        resolveWithPayload = false,
        shouldCancel,
        cancelResult = null
    } = options;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        if (shouldCancel?.()) {
            return cancelResult;
        }

        const response = await ApiHelper.get(`/api/jobs/status/${jobId}`);
        const payload = response?.data;

        const status = payload?.status;

        if (status === PollJobStatus.COMPLETED) {
            if (resolveWithPayload) {
                return payload;
            }
            return payload?.result ?? null;
        }

        if (status === PollJobStatus.FAILED || status === PollJobStatus.NOT_FOUND) {
            throw new Error(payload?.error || failureMessage);
        }

        await new Promise(resolve => setTimeout(resolve, pollInterval));
    }

    throw new Error(timeoutMessage);
}
